import { useQuery } from '@tanstack/react-query';
import Select, {
  MultiValue,
  ValueContainerProps,
  components,
} from 'react-select';
import ProductCards from '../components/ProductCards';
import { getAccessories } from '../service/getAccessories';
import { useContext, useState } from 'react';
import {
  NonScriptRangeProps,
  ReactSelectOptionProp,
} from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import showToast from './ToastComponent';
import { getProductBrand } from '../service/getProductBrands';
import Loading from './Loading';
import { CartContext } from '../context/cartContext';
import { ProductBrands } from '../constant/constant';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ValueContainer = ({ children, ...props }: ValueContainerProps<any>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let [values, input] = children as any;

  if (Array.isArray(values)) {
    const plural = values.length === 1 ? '' : 's';
    values = `${values.length} item${plural} selected`;
  }

  return (
    <components.ValueContainer {...props}>
      {values}
      {input}
    </components.ValueContainer>
  );
};

export function NonScriptRangeProducts(props: NonScriptRangeProps) {
  const [brands, setBrands] = useState<MultiValue<ReactSelectOptionProp>>([]);
  const [selectedBrand, setSelectedBrand] = useState<
    Array<ReactSelectOptionProp>
  >([]);
  const { user } = useAuth();
  const { cart } = useContext(CartContext);

  const { data, isLoading } = useQuery({
    queryKey: ['accessories', user?.access_token, selectedBrand, cart],
    queryFn: async () => {
      const accessories = await getAccessories(
        user!.access_token,
        selectedBrand,
        cart,
      );
      if (!accessories)
        showToast({
          message: 'Unable to fetch accessories!',
          type: 'info',
        });
      else return accessories;

      return [];
    },
    enabled: !!user?.access_token,
  });

  const { isLoading: isBrandLoading } = useQuery({
    queryKey: ['productBrand', user?.access_token],
    queryFn: async () => {
      const result = await getProductBrand(user!.access_token);
      if (typeof result === 'number') {
        showToast({
          message: 'Unable to fetch product brand!',
          type: 'error',
        });
        return null;
      }

      const filteredBrands = result
        .filter(
          (x: { product_brand: string }) =>
            x.product_brand !== ProductBrands.ATLUS,
        )
        .map((e: { product_brand: string }) => ({
          label: e.product_brand,
          value: e.product_brand,
        }));
      setBrands(filteredBrands);
      return null;
    },
  });

  if (isLoading || isBrandLoading || props.loading) return <Loading />;

  return (
    <div className="result-block-container">
      <div className="flex justify-start md:justify-end">
        <div className="flex items-center mb-5">
          <small>Brand</small>
          <Select
            classNames={{
              control: (state) =>
                `${
                  state.isFocused
                    ? 'border-grey-400 border text-sm'
                    : 'border-grey-400 border text-sm'
                } w-60 cursor-pointer ml-2`,
            }}
            value={selectedBrand}
            options={brands}
            onChange={(selected) => {
              setSelectedBrand([...selected]);
            }}
            isSearchable
            hideSelectedOptions={false}
            isMulti
            isClearable
            components={{ ValueContainer }}
            isLoading={isBrandLoading}
          />
        </div>
      </div>

      <ul className="grid grid-cols-1 gap-6 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
        {data && data?.length > 0 ? (
          data?.map((product: any, index: number) => (
            <div key={index}>
              <ProductCards
                addProductToCart={async (id: string) => {
                  await props.addToCartMutation.mutateAsync(id);
                }}
                removeProductFromCart={async (productId: string) => {
                  await props.removeFromCartMutation.mutateAsync(productId);
                }}
                product={product}
                substitueCard={false}
                isAccessories={true}
                allowedProduct={false}
              />
            </div>
          ))
        ) : (
          <p>No Non-Script Range Products Exist </p>
        )}
      </ul>
    </div>
  );
}
